import { useMemo, useState, type FormEvent } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { FileText, ShoppingCart, PlayCircle, Minus, Plus, CheckCircle2, Clock, ShieldCheck, GraduationCap, Mail } from 'lucide-react';
import {
  APPLIANCE_BASE_PRICE_USD,
  CLOUD_PLANS,
  FEATURE_METADATA,
  ORDER_CATALOG,
  isLicensableFeature,
  ORDER_SOFTWARE_OPTIONS,
  findCloudPlan,
  type CloudPlanId,
  type FormReceipt,
  type OrderRequest,
  type SoftwareTier,
} from '@scada-ink/shared/cloud';
import { Button, Container } from '@/components/ui';
import { formatUsd } from '@/components/LicenseBuilder';
import { Alert, Honeypot, SelectField, TextArea, TextField } from '@/components/form';
import { usePageTitle } from '@/components/layout/SiteLayout';
import { cloudApi, errorMessage } from '@/lib/cloudApi';
import { INK_U_URL, SALES_EMAIL } from '@/lib/site';

type Intent = OrderRequest['intent'];

const INTENTS: Array<{ id: Intent; icon: typeof FileText; title: string; body: string }> = [
  { id: 'quote', icon: FileText, title: 'Request a quote', body: 'Pricing for your appliances, tier and services' },
  { id: 'order', icon: ShoppingCart, title: 'Place an order', body: 'Ready to buy — we confirm and invoice' },
  { id: 'demo', icon: PlayCircle, title: 'Book a demo', body: 'A live walkthrough with an engineer' },
];

const isIntent = (v: string | null): v is Intent => v === 'quote' || v === 'order' || v === 'demo';
const isTier = (v: string | null): v is SoftwareTier => ORDER_SOFTWARE_OPTIONS.some((t) => t.id === v);

function builderNotes(params: URLSearchParams): string {
  if (params.get('from') !== 'builder') return '';
  const features = (params.get('features') ?? '').split(',').filter(isLicensableFeature);
  const picked = params.get('all') === '1'
    ? 'All features'
    : features.length ? features.map((f) => FEATURE_METADATA[f].name).join(', ') : 'Core platform only';
  const maintenance = params.get('maintenance') === '0' ? 'without maintenance' : 'with maintenance';
  return `New system from the License Builder: ${formatUsd(APPLIANCE_BASE_PRICE_USD)} base + ${picked}, ${maintenance}.`;
}

function Stepper({ value, onChange }: { value: number; onChange: (n: number) => void }) {
  return (
    <div className="inline-flex items-center rounded-lg border border-glass-border-bright bg-white">
      <button type="button" aria-label="Decrease" onClick={() => onChange(Math.max(0, value - 1))} className="p-2 text-text-secondary hover:text-text-primary disabled:opacity-40" disabled={value === 0}>
        <Minus size={14} />
      </button>
      <input
        type="number"
        min={0}
        max={10000}
        value={value}
        onChange={(e) => onChange(Math.max(0, Math.min(10000, Number(e.target.value) || 0)))}
        className="w-12 text-center text-sm font-semibold bg-transparent outline-none [appearance:textfield] [&::-webkit-inner-spin-button]:appearance-none"
        aria-label="Quantity"
      />
      <button type="button" aria-label="Increase" onClick={() => onChange(Math.min(10000, value + 1))} className="p-2 text-text-secondary hover:text-text-primary">
        <Plus size={14} />
      </button>
    </div>
  );
}

function Panel({ step, title, children }: { step: number; title: string; children: React.ReactNode }) {
  return (
    <section className="glass-panel p-6 lg:p-7">
      <div className="flex items-center gap-3 mb-5">
        <span className="w-7 h-7 rounded-full bg-gradient-to-br from-brand-primary to-brand-secondary text-white text-xs font-bold flex items-center justify-center">{step}</span>
        <h2 className="text-lg font-bold">{title}</h2>
      </div>
      {children}
    </section>
  );
}

export function OrderPage() {
  const [params, setParams] = useSearchParams();
  const intent: Intent = isIntent(params.get('intent')) ? (params.get('intent') as Intent) : 'quote';
  usePageTitle(INTENTS.find((i) => i.id === intent)!.title);

  const [contact, setContact] = useState({ name: '', email: '', company: '', phone: '', country: '' });
  const [tier, setTier] = useState<SoftwareTier>(isTier(params.get('tier')) ? (params.get('tier') as SoftwareTier) : 'professional');
  const [quantities, setQuantities] = useState<Record<string, number>>(() => Object.fromEntries(ORDER_CATALOG.map((i) => [i.sku, i.defaultQuantity])));
  const initialPlan = params.get('plan');
  const [plans, setPlans] = useState<CloudPlanId[]>(initialPlan && findCloudPlan(initialPlan) ? [initialPlan as CloudPlanId] : []);
  const [wantsTraining, setWantsTraining] = useState(true);
  const [timeline, setTimeline] = useState('1-3 months');
  // A system configured in the License Builder arrives as its selection, and
  // is written into the notes so the quote is for exactly what was priced.
  const [notes, setNotes] = useState(() => builderNotes(params));
  const [partnerCode, setPartnerCode] = useState(params.get('partner') ?? '');
  const [trap, setTrap] = useState('');
  const [sending, setSending] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [receipt, setReceipt] = useState<FormReceipt | null>(null);

  const lineItems = useMemo(() => ORDER_CATALOG.filter((i) => (quantities[i.sku] ?? 0) > 0), [quantities]);
  const appliances = quantities['SI-APP-CM4'] ?? 0;

  const setIntent = (next: Intent) => {
    const p = new URLSearchParams(params);
    p.set('intent', next);
    setParams(p, { replace: true });
  };

  async function submit(e: FormEvent) {
    e.preventDefault();
    if (intent !== 'demo' && appliances === 0 && lineItems.length === 0 && plans.length === 0) {
      setError('Add at least one appliance, accessory or cloud plan — or choose “Book a demo”.');
      return;
    }
    setSending(true);
    setError(null);
    const body: OrderRequest & { website_url: string } = {
      ...contact,
      intent,
      softwareTier: tier,
      items: lineItems.map((i) => ({ sku: i.sku, quantity: quantities[i.sku] })),
      cloudPlans: plans,
      wantsTraining,
      timeline,
      notes,
      partnerCode: partnerCode || undefined,
      website_url: trap,
    };
    try {
      setReceipt(await cloudApi.post<FormReceipt>('/api/public/orders', body));
      window.scrollTo({ top: 0, behavior: 'smooth' });
    } catch (err) {
      setError(errorMessage(err));
    } finally {
      setSending(false);
    }
  }

  if (receipt) {
    const heading = { quote: 'Quote request received', order: 'Order received', demo: 'Demo request received' }[intent];
    return (
      <Container className="py-20 lg:py-28">
        <div className="max-w-2xl mx-auto glass-panel p-8 lg:p-12 text-center">
          <CheckCircle2 size={48} className="mx-auto text-status-running" />
          <h1 className="mt-5 text-3xl font-bold">{heading}</h1>
          <p className="mt-3 text-text-secondary">
            Thank you, {contact.name.split(' ')[0] || 'there'}. Your reference is <span className="font-mono font-semibold text-text-primary">{receipt.reference}</span>. A
            Scada.Ink specialist will reply to <b>{contact.email}</b> within one business day.
          </p>
          <div className="mt-8 grid sm:grid-cols-2 gap-3 text-left">
            <a href={INK_U_URL} target="_blank" rel="noopener noreferrer" className="glass-card p-4 hover:shadow-md transition-shadow">
              <GraduationCap size={20} className="text-brand-secondary" />
              <div className="mt-2 text-sm font-semibold">Get a head start on Ink U</div>
              <div className="text-xs text-text-secondary">Free courses while you wait for hardware.</div>
            </a>
            <Link to="/register" className="glass-card p-4 hover:shadow-md transition-shadow">
              <ShieldCheck size={20} className="text-brand-primary" />
              <div className="mt-2 text-sm font-semibold">Create your portal account</div>
              <div className="text-xs text-text-secondary">Cloud plans, alerts and appliance health.</div>
            </Link>
          </div>
        </div>
      </Container>
    );
  }

  return (
    <div className="bg-ink-base">
      <div className="bg-white border-b border-glass-border">
        <Container className="py-10 lg:py-12">
          <h1 className="text-3xl sm:text-4xl font-extrabold tracking-tight">Let’s build your system</h1>
          <p className="mt-3 text-lg text-text-secondary max-w-2xl">Tell us what you need. A specialist replies within one business day — no obligation.</p>
          <div className="mt-8 grid sm:grid-cols-3 gap-3" role="tablist">
            {INTENTS.map((i) => (
              <button
                key={i.id}
                type="button"
                role="tab"
                aria-selected={intent === i.id}
                onClick={() => setIntent(i.id)}
                className={`flex items-start gap-3 rounded-[var(--radius-glass-sm)] border p-4 text-left transition-all ${
                  intent === i.id ? 'border-brand-primary bg-brand-primary/5 ring-4 ring-brand-primary/10' : 'border-glass-border bg-white hover:border-glass-border-bright'
                }`}
              >
                <i.icon size={20} className={intent === i.id ? 'text-brand-primary' : 'text-text-muted'} />
                <span>
                  <span className="block text-sm font-semibold">{i.title}</span>
                  <span className="block text-xs text-text-secondary mt-0.5">{i.body}</span>
                </span>
              </button>
            ))}
          </div>
        </Container>
      </div>

      <Container className="py-10 lg:py-14">
        <form onSubmit={submit} className="relative grid lg:grid-cols-[1fr_340px] gap-8 items-start">
          <Honeypot value={trap} onChange={setTrap} />
          <div className="space-y-6">
            <Panel step={1} title="Your details">
              <div className="grid sm:grid-cols-2 gap-4">
                <TextField label="Full name" required autoComplete="name" value={contact.name} onChange={(e) => setContact({ ...contact, name: e.target.value })} />
                <TextField label="Work email" type="email" required autoComplete="email" value={contact.email} onChange={(e) => setContact({ ...contact, email: e.target.value })} />
                <TextField label="Company" required autoComplete="organization" value={contact.company} onChange={(e) => setContact({ ...contact, company: e.target.value })} />
                <TextField label="Phone" type="tel" autoComplete="tel" value={contact.phone} onChange={(e) => setContact({ ...contact, phone: e.target.value })} />
                <TextField label="Country / region" autoComplete="country-name" value={contact.country} onChange={(e) => setContact({ ...contact, country: e.target.value })} />
                <SelectField
                  label="Timeline"
                  value={timeline}
                  onChange={(e) => setTimeline(e.target.value)}
                  options={['Immediately', '1-3 months', '3-6 months', '6+ months', 'Just researching'].map((v) => ({ value: v, label: v }))}
                />
              </div>
            </Panel>

            <Panel step={2} title="Software tier">
              <div className="grid sm:grid-cols-2 xl:grid-cols-4 gap-3">
                {ORDER_SOFTWARE_OPTIONS.map((t) => (
                  <label
                    key={t.id}
                    className={`relative cursor-pointer rounded-[var(--radius-glass-sm)] border p-4 transition-all ${
                      tier === t.id ? 'border-brand-primary bg-brand-primary/5 ring-4 ring-brand-primary/10' : 'border-glass-border hover:border-glass-border-bright'
                    }`}
                  >
                    <input type="radio" name="tier" value={t.id} checked={tier === t.id} onChange={() => setTier(t.id)} className="sr-only" />
                    <span className="block text-sm font-bold">{t.name}</span>
                    <span className="block text-xs text-text-secondary mt-1 leading-snug">{t.summary}</span>
                  </label>
                ))}
              </div>
              <p className="mt-3 text-xs text-text-muted">
                Not sure? Leave Professional selected — we will recommend a tier from your notes. <Link to="/pricing#tiers" className="text-brand-primary font-semibold">Compare tiers</Link>
              </p>
            </Panel>

            <Panel step={3} title="Hardware & services">
              <div className="divide-y divide-glass-border -my-3">
                {ORDER_CATALOG.map((item) => (
                  <div key={item.sku} className="flex items-center gap-4 py-3">
                    <div className="flex-1 min-w-0">
                      <div className="flex flex-wrap items-baseline gap-x-2">
                        <span className="text-sm font-semibold">{item.name}</span>
                        <span className="text-[11px] font-mono text-text-muted">{item.sku}</span>
                      </div>
                      <p className="text-xs text-text-secondary mt-0.5">{item.description}</p>
                    </div>
                    <Stepper value={quantities[item.sku] ?? 0} onChange={(n) => setQuantities({ ...quantities, [item.sku]: n })} />
                  </div>
                ))}
              </div>
            </Panel>

            <Panel step={4} title="Scada.Ink Cloud (optional)">
              <div className="grid sm:grid-cols-2 gap-3">
                {CLOUD_PLANS.map((p) => {
                  const checked = plans.includes(p.id);
                  return (
                    <label
                      key={p.id}
                      className={`flex items-start gap-3 cursor-pointer rounded-[var(--radius-glass-sm)] border p-3.5 transition-all ${
                        checked ? 'border-brand-primary bg-brand-primary/5' : 'border-glass-border hover:border-glass-border-bright'
                      }`}
                    >
                      <input
                        type="checkbox"
                        checked={checked}
                        onChange={() => setPlans(checked ? plans.filter((x) => x !== p.id) : [...plans, p.id])}
                        className="mt-1 accent-[#3b82f6]"
                      />
                      <span className="flex-1">
                        <span className="flex items-baseline justify-between gap-2">
                          <span className="text-sm font-semibold">{p.name}</span>
                          <span className="text-xs text-text-muted whitespace-nowrap">
                            ${p.priceMonthlyUsd}/{p.unit === 'appliance' ? 'appl./' : ''}mo
                          </span>
                        </span>
                        <span className="block text-xs text-text-secondary mt-0.5">{p.tagline}</span>
                      </span>
                    </label>
                  );
                })}
              </div>
              <p className="mt-3 text-xs text-text-muted">
                Cloud plans can also be subscribed to directly in the <Link to="/register" className="text-brand-primary font-semibold">customer portal</Link>.
              </p>
            </Panel>

            <Panel step={5} title="Anything else?">
              <label className="flex items-start gap-3 cursor-pointer mb-5">
                <input type="checkbox" checked={wantsTraining} onChange={(e) => setWantsTraining(e.target.checked)} className="mt-1 accent-[#3b82f6]" />
                <span>
                  <span className="block text-sm font-semibold">Include a training plan</span>
                  <span className="block text-xs text-text-secondary">We will suggest Ink U courses and certification vouchers for your team.</span>
                </span>
              </label>
              <div className="grid gap-4">
                <TextArea
                  label="Your machines and goals"
                  rows={4}
                  value={notes}
                  onChange={(e) => setNotes(e.target.value)}
                  placeholder="e.g. 12 CNCs (Fanuc 0i and Haas), 3 CompactLogix PLCs; we want OEE by shift and SMS alarms."
                />
                <TextField label="Partner code" value={partnerCode} onChange={(e) => setPartnerCode(e.target.value)} hint="If a Scada.Ink partner referred you." />
              </div>
            </Panel>
          </div>

          {/* Summary */}
          <aside className="lg:sticky lg:top-32 space-y-4">
            <div className="glass-panel p-6">
              <h2 className="text-sm font-bold uppercase tracking-[0.14em] text-text-muted">Summary</h2>
              <dl className="mt-4 space-y-2.5 text-sm">
                <div className="flex justify-between gap-3">
                  <dt className="text-text-secondary">Request</dt>
                  <dd className="font-semibold">{INTENTS.find((i) => i.id === intent)!.title}</dd>
                </div>
                <div className="flex justify-between gap-3">
                  <dt className="text-text-secondary">Software tier</dt>
                  <dd className="font-semibold">{ORDER_SOFTWARE_OPTIONS.find((t) => t.id === tier)!.name}</dd>
                </div>
                {lineItems.map((i) => (
                  <div key={i.sku} className="flex justify-between gap-3">
                    <dt className="text-text-secondary truncate">{i.name}</dt>
                    <dd className="font-semibold tabular-nums">× {quantities[i.sku]}</dd>
                  </div>
                ))}
                {plans.map((id) => (
                  <div key={id} className="flex justify-between gap-3">
                    <dt className="text-text-secondary truncate">{findCloudPlan(id)!.name}</dt>
                    <dd className="font-semibold">${findCloudPlan(id)!.priceMonthlyUsd}/mo</dd>
                  </div>
                ))}
              </dl>
              {error && (
                <div className="mt-4">
                  <Alert kind="error">{error}</Alert>
                </div>
              )}
              <Button type="submit" size="lg" className="w-full mt-6" disabled={sending} arrow>
                {sending ? 'Sending…' : { quote: 'Request quote', order: 'Submit order', demo: 'Request demo' }[intent]}
              </Button>
              <p className="mt-3 text-[11px] text-text-muted text-center">No payment is taken here. We confirm pricing before anything ships.</p>
            </div>
            <div className="glass-card p-5 space-y-3 text-sm">
              <div className="flex items-start gap-2.5">
                <Clock size={16} className="text-brand-primary mt-0.5" />
                <span className="text-text-secondary">Reply within one business day</span>
              </div>
              <div className="flex items-start gap-2.5">
                <GraduationCap size={16} className="text-brand-primary mt-0.5" />
                <span className="text-text-secondary">Free Ink U training for your whole team</span>
              </div>
              <div className="flex items-start gap-2.5">
                <Mail size={16} className="text-brand-primary mt-0.5" />
                <a href={`mailto:${SALES_EMAIL}`} className="text-text-secondary hover:text-brand-primary">
                  {SALES_EMAIL}
                </a>
              </div>
            </div>
          </aside>
        </form>
      </Container>
    </div>
  );
}
